import WhiteTees from "@/components/mall/rooms/WhiteTees";
import GangSheets from "@/components/mall/rooms/GangSheets";
import Apparel from "@/components/mall/rooms/Apparel";
import SignsRoom from "@/components/mall/rooms/SignsRoom";
import Artwork from "@/components/mall/rooms/Artwork";
import Classifieds from "@/components/mall/rooms/Classifieds";
import ClassesRoom from "@/components/mall/rooms/ClassesRoom";
import Website from "@/components/mall/rooms/Website";

const apparel = (s) => !!s?.sells?.apparel;

/**
 * One entry per service. The shop home page shows them as a card grid; each destination page
 * hangs the same billboard at its top as the banner, with the headline + one-liner under it.
 * A shop can override `headline`, `line` and `image` per key through `store.page_banners[key]`.
 */
export const BANNERS = [
    { key: "white", Room: WhiteTees, title: "Same-day white tees", hook: "From $15 a shirt · order by 2 PM",
      headline: "White shirts, printed today.", line: "Order and pay before 2 PM and your shirts are ready tonight. One shirt is $15.",
      image: "/assets/mascot.png", accent: "#2563EB", dark: true, gate: apparel,
      to: (q) => `/shirts?ready=same_day${q ? `&store=${q}` : ""}` },
    { key: "apparel", Room: Apparel, title: "Standard apparel", hook: "Black, grey, red, royal · up to 4 weeks",
      headline: "Every tee, tank and hoodie in color.", line: "Six styles, six colors, printed to order. Unlimited free color changes and resizing.",
      image: "https://images.unsplash.com/photo-1716541424893-734612ddcabb?crop=entropy&cs=srgb&fm=jpg&q=85&w=900&h=700&fit=crop",
      accent: "#0B0B0B", gate: apparel, to: (q) => `/shirts${q ? `?store=${q}` : ""}` },
    { key: "dtf", Room: GangSheets, title: "DTF gang sheets", hook: "$5 a linear ft · any length",
      headline: "All of your designs on one sheet.", line: "Upload everything, we nest it, you press it on anything. The more you fit, the less each one costs.",
      image: "/assets/dtf-mascot.jpg", accent: "#1d4ed8", gate: (s) => !!s?.sells?.dtf, to: (q) => `/shirts/dtf${q ? `?store=${q}` : ""}` },
    { key: "signs", Room: SignsRoom, title: "Signs", hook: "From $2 a sq ft",
      headline: "Banners, boards, decals and cards.", line: "Pick a material, type your size, upload your art. Most signs are ready in 3 to 5 business days.",
      image: "/api/files/43f93f36-0a06-4cc6-afed-249afc6d9b44", accent: "#0B0B0B", gate: (s) => !!s?.sells?.signs,
      to: (q) => `/signs${q ? `?store=${q}` : ""}` },
    { key: "classifieds", Room: Classifieds, title: "Classifieds", hook: "Buy & sell used gear",
      headline: "Used machines, supplies and blanks.", line: "Every ad says who is selling and where. Message the seller and make the deal yourselves.",
      image: (ctx) => ctx.floor?.classifieds?.find((a) => a.image)?.image || "/assets/storefront.png", accent: "#B45309",
      to: () => "/supplies" },
    { key: "classes", Room: ClassesRoom, title: "Classes", hook: "$99 · 2-hour workshop",
      headline: "Learn to print, in a real shop.", line: "Hands-on garment printing workshop, three seats per class. Book a seat and pay the shop.",
      image: "/api/files/8072bc2e-5b84-4a7e-b413-2aee29321457", accent: "#0F766E", to: () => "/classes" },
    { key: "artwork", Room: Artwork, title: "Artwork", hook: "$24 AI recreation",
      headline: "No art? No problem.", line: "Send us what you have. Print-ready files are free to check; a blurry logo is rebuilt with AI for $24.",
      image: "/assets/art/ballers.webp", accent: "#DC2626", to: (q) => `/artwork-request${q ? `?store=${q}` : ""}` },
    { key: "website", Room: Website, title: "Websites", hook: "Live in 7 days",
      headline: "A custom website for any business.", line: "Built with AI, live in seven days, kept running and secured for one monthly fee.",
      image: null, accent: "#22D3EE", dark: true, gate: (s) => !!s?.builds_websites, to: () => "/website-design" },
    { key: "wholesale", Room: null, title: "Coupons & wholesale", hook: "Shop coupons & Gang pricing",
      headline: "Coupons and bulk prices.", line: "This shop's live coupons, plus the Gang — a standing discount at every shop that honors it.",
      image: null, accent: "#7C3AED", to: () => "/wholesale" },
];

export const BANNER_BY_KEY = Object.fromEntries(BANNERS.map((b) => [b.key, b]));

/** The cards this shop shows — only the services it actually offers. */
export const shopCards = (store) => BANNERS.filter((b) => !b.gate || b.gate(store));

/** Banner copy for a page, with the shop's own override on top. */
export const bannerCopy = (key, store) => {
    const base = BANNER_BY_KEY[key];
    const over = store?.page_banners?.[key] || {};
    return { ...base, headline: over.headline || base.headline, line: over.line || base.line, image: over.image || base.image };
};
